import { motion } from 'framer-motion';
import { FileSpreadsheet, FileText, Upload, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { Button } from '../components/Button';

interface DBFField {
  name: string;
  type: string;
  length: number;
  decimal: number;
}

interface DBFRecord {
  [key: string]: any;
}

interface DBFData {
  fields: DBFField[];
  records: DBFRecord[];
}

// Simple DBF parser function
function parseDBF(buffer: ArrayBuffer): DBFData {
  const view = new DataView(buffer);

  // Read header
  const recordCount = view.getUint32(4, true);
  const headerLength = view.getUint16(8, true);
  const recordLength = view.getUint16(10, true);

  // Read field descriptors
  const fields: DBFField[] = [];
  let offset = 32; // Start after header

  while (offset < headerLength - 1) {
    const fieldName = new TextDecoder('ascii')
      .decode(buffer.slice(offset, offset + 11))
      .replace(/\0/g, '');

    if (fieldName === '') break;

    const fieldType = String.fromCharCode(view.getUint8(offset + 11));
    const fieldLength = view.getUint8(offset + 16);
    const fieldDecimal = view.getUint8(offset + 17);

    fields.push({
      name: fieldName,
      type: fieldType,
      length: fieldLength,
      decimal: fieldDecimal,
    });

    offset += 32;
  }

  // Read records
  const records: DBFRecord[] = [];
  const recordStart = headerLength;

  for (let i = 0; i < Math.min(recordCount, 1000); i++) {
    // Limit to 1000 records for performance
    const recordOffset = recordStart + i * recordLength;
    const record: DBFRecord = {};

    // Check if record is deleted (first byte is 0x2A for deleted records)
    if (view.getUint8(recordOffset) === 0x2a) continue;

    let fieldOffset = 1; // Skip deletion flag

    for (const field of fields) {
      const value = new TextDecoder('ascii')
        .decode(
          buffer.slice(
            recordOffset + fieldOffset,
            recordOffset + fieldOffset + field.length
          )
        )
        .trim();

      record[field.name] = value || null;
      fieldOffset += field.length;
    }

    records.push(record);
  }

  return { fields, records };
}

export function DBFViewer(): React.JSX.Element {
  useEffect(() => {
    document.title = 'DBF Viewer | NiveshRight';
  }, []);

  const [dbfData, setDbfData] = useState<DBFData | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!file.name.toLowerCase().endsWith('.dbf')) {
        setError('Please select a valid DBF file');
        return;
      }

      setIsLoading(true);
      setError('');
      setFileName(file.name);

      try {
        const arrayBuffer = await file.arrayBuffer();

        // Parse DBF file manually
        const data = parseDBF(arrayBuffer);

        setDbfData({
          fields: data.fields,
          records: data.records,
        });
      } catch (err) {
        console.error('Error parsing DBF file:', err);
        setError(
          "Failed to parse DBF file. Please ensure it's a valid DBF file."
        );
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const exportToCSV = useCallback(() => {
    if (!dbfData) return;

    const headers = dbfData.fields.map(field => field.name);
    const csvContent = [
      headers.join(','),
      ...dbfData.records.map(record =>
        headers
          .map(header => {
            const value = record[header];
            // Escape CSV values that contain commas or quotes
            if (
              typeof value === 'string' &&
              (value.includes(',') || value.includes('"'))
            ) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value || '';
          })
          .join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${fileName.replace('.dbf', '')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [dbfData, fileName]);

  const exportToXLS = useCallback(() => {
    if (!dbfData) return;

    // Import XLSX dynamically
    import('xlsx').then(XLSX => {
      const worksheet = XLSX.utils.json_to_sheet(dbfData.records);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');

      XLSX.writeFile(workbook, `${fileName.replace('.dbf', '')}.xlsx`);
    });
  }, [dbfData, fileName]);

  const clearData = useCallback(() => {
    setDbfData(null);
    setFileName('');
    setError('');
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <main className="w-full px-2 sm:px-4 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl p-4 sm:p-8 shadow-lg border border-gray-100"
        >
          {/* File Upload Section */}
          <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center mb-8 hover:border-blue-400 transition-colors">
            <input
              type="file"
              accept=".dbf"
              onChange={handleFileUpload}
              className="hidden"
              id="dbf-file-input"
            />
            <label
              htmlFor="dbf-file-input"
              className="cursor-pointer flex flex-col items-center"
            >
              <Upload className="h-16 w-16 text-gray-400 mb-4" />
              <span className="text-xl font-semibold text-gray-700 mb-2">
                Click to upload DBF file
              </span>
              <span className="text-sm text-gray-500">
                or drag and drop your file here
              </span>
            </label>
          </div>

          {isLoading && (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Processing file...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <X className="h-5 w-5 text-red-400 mr-2" />
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          )}

          {dbfData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-6"
            >
              {/* File Info and Actions */}
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {fileName}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {dbfData.fields.length} fields, {dbfData.records.length}{' '}
                      records
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button onClick={exportToCSV} icon={FileText}>
                      Export CSV
                    </Button>
                    <Button
                      onClick={exportToXLS}
                      variant="secondary"
                      icon={FileSpreadsheet}
                    >
                      Export Excel
                    </Button>
                    <Button onClick={clearData} variant="secondary" icon={X}>
                      Clear
                    </Button>
                  </div>
                </div>
              </div>

              {/* Data Preview */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Data Preview (First 100 records)
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {dbfData.fields.map((field, index) => (
                          <th
                            key={index}
                            className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                          >
                            {field.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {dbfData.records
                        .slice(0, 100)
                        .map((record, recordIndex) => (
                          <tr key={recordIndex} className="hover:bg-gray-50">
                            {dbfData.fields.map((field, fieldIndex) => (
                              <td
                                key={fieldIndex}
                                className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate"
                                title={record[field.name] || '-'}
                              >
                                {record[field.name] || '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                {dbfData.records.length > 100 && (
                  <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
                    <p className="text-sm text-gray-500">
                      Showing first 100 records of {dbfData.records.length}{' '}
                      total records
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </motion.div>
      </main>
    </div>
  );
}

export default DBFViewer;
